import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Switch,
  Link,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { customersAPI, environmentsAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';
import CustomerDocuments from '../components/CustomerDocuments';

function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canEdit, canCreate, canDelete, isAdmin, isSupport } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [locations, setLocations] = useState([]);

  const [contactDialog, setContactDialog] = useState({ open: false, contact: null });
  const [contactForm, setContactForm] = useState({
    email: '',
    name: '',
    role: '',
    notification_types: ['patches', 'reports'],
    is_active: true,
  });

  const [envDialog, setEnvDialog] = useState({ open: false, environment: null });
  const [envForm, setEnvForm] = useState({
    system_type: 'LIMS',
    environment_type: 'Development',
    url: '',
    is_active: true,
  });

  useEffect(() => {
    loadCustomer();
    loadLocations();
  }, [id]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.get(id);
      setCustomer(response.data);
      setContacts(response.data.contacts || []);
      setEnvironments(response.data.environments || []);
      setError(null);
    } catch (err) {
      setError('Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await customersAPI.locations();
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations', err);
    }
  };

  const handleSaveCustomer = async () => {
    setSaving(true);
    setError(null);

    try {
      await customersAPI.update(id, {
        cust_name: customer.cust_name,
        cl_number: customer.cl_number,
        location: customer.location,
        is_active: customer.is_active,
        notes: customer.notes,
        licence_start_date: customer.licence_start_date,
      });
      setSuccess('Customer updated successfully');
      loadCustomer();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenContactDialog = (contact = null) => {
    if (contact) {
      setContactForm({
        email: contact.email,
        name: contact.name || '',
        role: contact.role || '',
        notification_types: contact.notification_types || ['patches', 'reports'],
        is_active: contact.is_active,
      });
    } else {
      setContactForm({
        email: '',
        name: '',
        role: '',
        notification_types: ['patches', 'reports'],
        is_active: true,
      });
    }
    setContactDialog({ open: true, contact });
  };

  const handleSaveContact = async () => {
    try {
      if (contactDialog.contact) {
        // Update existing contact
        await customersAPI.updateContact(contactDialog.contact.id, contactForm);
        setSuccess('Contact updated successfully');
      } else {
        // Create new contact
        await customersAPI.createContact(id, contactForm);
        setSuccess('Contact added successfully');
      }
      setContactDialog({ open: false, contact: null });
      loadCustomer();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;

    try {
      await customersAPI.deleteContact(contactId);
      setSuccess('Contact deleted');
      loadCustomer();
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  const handleOpenEnvDialog = (environment = null) => {
    if (environment) {
      setEnvForm({
        system_type: environment.system_type,
        environment_type: environment.environment_type,
        url: environment.url,
        is_active: environment.is_active,
      });
    } else {
      setEnvForm({
        system_type: 'LIMS',
        environment_type: 'Development',
        url: '',
        is_active: true,
      });
    }
    setEnvDialog({ open: true, environment });
  };

  const handleSaveEnvironment = async () => {
    try {
      if (envDialog.environment) {
        // Update existing environment
        await environmentsAPI.update(envDialog.environment.id, envForm);
        setSuccess('Environment updated successfully');
      } else {
        // Create new environment
        await environmentsAPI.create({ ...envForm, customer_id: parseInt(id) });
        setSuccess('Environment added successfully');
      }
      setEnvDialog({ open: false, environment: null });
      loadCustomer();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save environment');
    }
  };

  const handleDeleteEnvironment = async (environmentId) => {
    if (!window.confirm('Delete this environment?')) return;

    try {
      await environmentsAPI.delete(environmentId);
      setSuccess('Environment deleted');
      loadCustomer();
    } catch (err) {
      setError('Failed to delete environment');
    }
  };

  const handleNotificationTypeChange = (type) => {
    const current = contactForm.notification_types || [];
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    setContactForm({ ...contactForm, notification_types: updated });
  };

  const formatUrl = (url) => {
    if (!url) return '';
    // Add https:// if no protocol is specified
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box>
        <Alert severity="error">Customer not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/customers')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          {customer.cust_code} - {customer.cust_name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Customer Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Customer Information
        </Typography>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Customer Code"
              value={customer.cust_code}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Customer Name"
              value={customer.cust_name}
              onChange={(e) => setCustomer({ ...customer, cust_name: e.target.value })}
              disabled={!canEdit()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="CL Number"
              value={customer.cl_number || ''}
              onChange={(e) => setCustomer({ ...customer, cl_number: e.target.value })}
              disabled={!canEdit()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              select
              label="Location"
              value={customer.location || ''}
              onChange={(e) => setCustomer({ ...customer, location: e.target.value })}
              disabled={!canEdit()}
            >
              <MenuItem value="">None</MenuItem>
              {locations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="License Start Date"
              type="date"
              value={customer.licence_start_date || ''}
              onChange={(e) => setCustomer({ ...customer, licence_start_date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={!canEdit()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={customer.is_active}
                  onChange={(e) => setCustomer({ ...customer, is_active: e.target.checked })}
                  disabled={!canEdit()}
                />
              }
              label="Active"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={customer.notes || ''}
              onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
              disabled={!canEdit()}
            />
          </Grid>
        </Grid>


        {canEdit() && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveCustomer}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Customer'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Environments */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">System Environments</Typography>
          {canCreate() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEnvDialog()}
            >
              Add Environment
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>System</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {environments.map((env) => (
                <TableRow key={env.id}>
                  <TableCell>
                    <Chip label={env.system_type} size="small" />
                  </TableCell>
                  <TableCell>{env.environment_type}</TableCell>
                  <TableCell>
                    <Link href={formatUrl(env.url)} target="_blank" rel="noopener">
                      {env.url}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={env.is_active ? 'Active' : 'Inactive'}
                      color={env.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {canEdit() && (
                      <IconButton size="small" onClick={() => handleOpenEnvDialog(env)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDelete() && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteEnvironment(env.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {environments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No environments added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Contacts */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Email Contacts</Typography>
          {canCreate() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenContactDialog()}
            >
              Add Contact
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Notifications</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>{contact.email}</TableCell>
                  <TableCell>{contact.name || '-'}</TableCell>
                  <TableCell>{contact.role || '-'}</TableCell>
                  <TableCell>
                    {(contact.notification_types || []).map((type) => (
                      <Chip key={type} label={type} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contact.is_active ? 'Active' : 'Inactive'}
                      color={contact.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {canEdit() && (
                      <IconButton size="small" onClick={() => handleOpenContactDialog(contact)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDelete() && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No contacts added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Documents (Cloud Services administrators and support) */}
      {(isAdmin() || isSupport()) && <CustomerDocuments customer={customer} />}

      {/* Contact Dialog */}
      <Dialog
        open={contactDialog.open}
        onClose={() => setContactDialog({ open: false, contact: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {contactDialog.contact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Role"
                value={contactForm.role}
                onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Email Notifications
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(contactForm.notification_types || []).includes('patches')}
                      onChange={() => handleNotificationTypeChange('patches')}
                    />
                  }
                  label="Patch Notifications"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(contactForm.notification_types || []).includes('reports')}
                      onChange={() => handleNotificationTypeChange('reports')}
                    />
                  }
                  label="Report Notifications"
                />
              </FormGroup>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.is_active}
                    onChange={(e) => setContactForm({ ...contactForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialog({ open: false, contact: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveContact} variant="contained">
            {contactDialog.contact ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Environment Dialog */}
      <Dialog
        open={envDialog.open}
        onClose={() => setEnvDialog({ open: false, environment: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {envDialog.environment ? 'Edit Environment' : 'Add Environment'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="System Type"
                value={envForm.system_type}
                onChange={(e) => setEnvForm({ ...envForm, system_type: e.target.value })}
              >
                <MenuItem value="LIMS">LIMS</MenuItem>
                <MenuItem value="SDMS">SDMS</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Environment Type"
                value={envForm.environment_type}
                onChange={(e) => setEnvForm({ ...envForm, environment_type: e.target.value })}
              >
                <MenuItem value="Development">Development</MenuItem>
                <MenuItem value="Test">Test</MenuItem>
                <MenuItem value="Production">Production</MenuItem>
                <MenuItem value="UAT">UAT</MenuItem>
                <MenuItem value="Staging">Staging</MenuItem>
                <MenuItem value="Training">Training</MenuItem>
                <MenuItem value="Demo">Demo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="URL"
                placeholder="https://customer.starlims.com"
                value={envForm.url}
                onChange={(e) => setEnvForm({ ...envForm, url: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={envForm.is_active}
                    onChange={(e) => setEnvForm({ ...envForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvDialog({ open: false, environment: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveEnvironment} variant="contained">
            {envDialog.environment ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerDetail;
